import {
  Badge,
  Flex,
  Skeleton,
  Switch,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type {
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount,
} from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'BrokerTsLib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import {
  useGetInsurerDiscounts,
  useSetInsurerValid,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useMemo, useState } from 'react'

import { TableRow } from '@/components'
import { TableSectionRow } from '@/components/Section/TableSectionRow'
import { useAuth } from '@/context/AuthContext'

import { DiscountEditModal } from './DiscountEditModal'

export const OrganizationInsurersTable = ({
  insurers,
  isLoading,
}: {
  insurers?: InsurerOrganizationUnit[]
  isLoading?: boolean
}) => {
  const { user } = useAuth()

  return (
    <Table variant={'simple'}>
      <Tbody>
        <TableSkeleton rowsCount={5} isLoaded={!isLoading}>
          <EmptyTablePlaceholder
            text={'Žádné přidělené pojišťovny'}
            colSpan={0}
            borderBottom={'none'}
          >
            {insurers &&
              insurers.map((insurer) => (
                <OrganizationInsurerTableRow
                  key={insurer.insurerId}
                  insurer={insurer}
                  editable={user?.allowEditOrganizationStructure}
                />
              ))}
          </EmptyTablePlaceholder>
        </TableSkeleton>
      </Tbody>
    </Table>
  )
}

const OrganizationInsurerTableRow = ({
  insurer,
  editable,
}: {
  insurer: InsurerOrganizationUnit
  editable?: boolean
}) => {
  const toast = useToast()
  const { mutateAsync: setValid } = useSetInsurerValid()
  const [toggleDisabled, setToggleDisabled] = useState(false)

  const toggleValid = () => {
    setToggleDisabled(true)
    setValid({
      insurerId: insurer.insurerId,
      insuranceCategoryType: insurer.insuranceCategoryType,
      valid: !insurer.valid,
      organizationUnitId: insurer.organizationUnitId,
    })
      .then(() => {
        setToggleDisabled(false)
        toast({
          title: 'Platnost pojišťovny byla změněna',
          status: 'success',
        })
      })
      .catch(() => {
        setToggleDisabled(false)
        toast({
          title: 'Chyba při změně platnosti pojišťovny',
          status: 'error',
        })
      })
  }

  // Sort discount by valid state, then by max discount
  const sortedActiveDiscounts = useMemo(
    () =>
      insurer.activeDiscounts
        .slice()
        .sort((a, b) => {
          if (a.valid === b.valid) {
            return b.maxDiscount - a.maxDiscount
          }
          return a.valid ? -1 : 1
        }),
    [insurer.activeDiscounts],
  )

  return (
    <TableSectionRow
      icon={
        editable && (
          <Switch
            size={'sm'}
            isChecked={insurer.valid}
            onChange={toggleValid}
            isDisabled={toggleDisabled}
          ></Switch>
        )
      }
      title={insurer.insurer}
    >
      <OrganizationInsurerDiscounts
        insurer={insurer}
        activeDiscounts={sortedActiveDiscounts}
        editable={editable}
      />
    </TableSectionRow>
  )
}

const OrganizationInsurerDiscounts = ({
  activeDiscounts,
  insurer,
  editable,
}: {
  activeDiscounts: InsurerOrganizationUnitDiscount[]
  insurer: InsurerOrganizationUnit
  editable?: boolean
}) => {
  const { data, isLoading } = useGetInsurerDiscounts(
    insurer.organizationUnitId,
    insurer.insurerId,
    insurer.insuranceCategoryType,
    {
      enabled: editable,
    },
  )

  const discounts = useMemo(() => {
    if (!editable || !data?.data) return activeDiscounts

    return [...data.data.assignedDiscounts, ...data.data.availableDiscounts]
  }, [editable, activeDiscounts, data?.data])

  return (
    <Flex flexWrap={'wrap'} gap={2}>
      {discounts.map((discount) => (
        <OrganizationInsurerDiscountBadge
          key={discount.insuranceCategoryDiscountId}
          discount={discount}
          discounts={discounts}
          insurer={insurer}
          editable={editable}
        />
      ))}
      <Skeleton isLoaded={!editable || !isLoading} />
    </Flex>
  )
}

const OrganizationInsurerDiscountBadge = ({
  discount,
  discounts,
  insurer,
  editable,
}: {
  discount: InsurerOrganizationUnitDiscount | InsuranceCategoryDiscount
  discounts: InsuranceCategoryDiscount[]
  insurer: InsurerOrganizationUnit
  editable?: boolean
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <CustomTooltip
        hasArrow
        p={0}
        label={
          <Table size="sm">
            <Thead>
              <TableRow>
                <Th colSpan={2} fontWeight="semibold" py={2}>
                  {discount.name}
                  {!('valid' in discount && discount.valid) && ' (neaktivní)'}
                </Th>
              </TableRow>
            </Thead>
            <Tbody fontSize="sm">
              <TableRow fontWeight="semibold">
                <Td>Maximální</Td>
                <Td>
                  {'maxDiscount' in discount
                    ? discount.maxDiscount
                    : discount.insurerMaxDiscount}{' '}
                  {discount.unit}
                </Td>
              </TableRow>
              {'defaultDiscount' in discount && (
                <TableRow>
                  <Td>Výchozí</Td>
                  <Td>
                    {discount.defaultDiscount} {discount.unit}
                  </Td>
                </TableRow>
              )}
              {'hiddenDiscount' in discount && (
                <TableRow>
                  <Td>Skrytá</Td>
                  <Td>
                    {discount.hiddenDiscount} {discount.unit}
                  </Td>
                </TableRow>
              )}
            </Tbody>
          </Table>
        }
      >
        <Badge
          onClick={() => editable && onOpen()}
          cursor={editable ? 'pointer' : 'cursor'}
          _hover={{ bg: 'neutral.100' }}
          colorScheme={
            (discount as InsurerOrganizationUnitDiscount)?.valid
              ? 'blue'
              : 'neutral'
          }
        >
          {'maxDiscount' in discount
            ? discount.maxDiscount
            : discount.insurerMaxDiscount}{' '}
          {'hiddenDiscount' in discount &&
            discount.hiddenDiscount > 0 &&
            '+ ' + discount.hiddenDiscount + ' '}
          % {discount.name}
        </Badge>
      </CustomTooltip>
      <DiscountEditModal
        discount={discount}
        discounts={discounts}
        insurer={insurer}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  )
}
